import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

interface VideoModalProps {
  src: string;
  onClose: () => void;
}

const VideoModal = ({ src, onClose }: VideoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKey);
      previousFocus?.focus();
    };
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div
      onClick={handleBackdropClick}
      className="backdrop-enter"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Video player"
        tabIndex={-1}
        className="modal-enter"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '900px',
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#000',
          outline: 'none',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close video"
          className="press"
          style={{
            position: 'absolute', top: '10px', right: '12px', zIndex: 1,
            background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
            fontSize: '20px', lineHeight: 1, cursor: 'pointer',
            borderRadius: '50%', width: '32px', height: '32px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ×
        </button>
        <video
          ref={videoRef}
          src={src}
          controls
          autoPlay
          muted
          playsInline
          style={{ display: 'block', width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </div>
    </div>,
    document.body
  );
};

export default VideoModal;
